import { OrderReportDimensions } from '@webapp/modules/reports/entities/enums/OrderReportDimensionEnum'
import { ReportChartMetrics } from '@webapp/modules/reports/entities/enums/ReportChartMetricEnum'
import { ReportKinds } from '@webapp/modules/reports/entities/enums/ReportKindEnum'
import messages from './messages'
import {
  type ExpenseReportConfig,
  type OrderReportConfig,
} from './ReportConfig'

export const vendorReportConfig: OrderReportConfig = {
  key: 'vendor',
  kind: ReportKinds.ORDERS,
  dimension: OrderReportDimensions.VENDORS,
  titleKey: messages.vendorReportTitle,
  sectionHeadingKey: messages.ordersSectionHeading,
  exportButtonLabelKey: messages.exportOrdersButtonLabel,
  exportTypeLabelKey: messages.orderItemsExportTypeLabel,
  chartHeadingKey: messages.topVendorsChartHeading,
  detailNotFoundHeaderKey: messages.vendorNotFoundHeader,
  tiles: [
    {
      labelKey: messages.totalSpendingTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalVendorsUsedTileLabel,
      detailLabelKey: messages.totalAccountCodesUsedTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
    {
      labelKey: messages.totalItemsPurchasedTileLabel,
      detailLabelKey: messages.numberOfItemsPurchasedTileLabel,
      metric: ReportChartMetrics.COUNT,
      chartType: 'line',
      format: 'count',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.SPENT,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-account__account_code__code',
  aggregatePage: 'OrdersByVendorReportPage',
  detailPage: 'OrdersByVendorReportDetailsPage',
  referrerType: 'orders.vendor',
  paginationIds: {
    aggregate: 'REPORT_VENDOR_SPEND',
    lineItems: 'REPORT_ORDER_LINE_ITEMS',
  },
}

export const userReportConfig: OrderReportConfig = {
  key: 'user',
  kind: ReportKinds.ORDERS,
  dimension: OrderReportDimensions.USERS,
  titleKey: messages.userReportTitle,
  sectionHeadingKey: messages.ordersSectionHeading,
  exportButtonLabelKey: messages.exportOrdersButtonLabel,
  exportTypeLabelKey: messages.orderItemsExportTypeLabel,
  chartHeadingKey: messages.topSpendChartHeading,
  detailNotFoundHeaderKey: messages.userNotFoundHeader,
  aggregateChart: {
    type: 'bar',
    categoryAxisLabelKey: messages.usersAxisLabel,
  },
  tiles: [
    {
      labelKey: messages.totalSpendingTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalItemsPurchasedTileLabel,
      metric: ReportChartMetrics.COUNT,
      chartType: 'line',
      format: 'count',
    },
    {
      labelKey: messages.spendByUserTileLabel,
      detailLabelKey: messages.accountCodesUsedTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.USAGE,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-total_cost',
  aggregatePage: 'OrdersByUserReportPage',
  detailPage: 'OrdersByUserReportDetailsPage',
  referrerType: 'orders.user',
  paginationIds: {
    aggregate: 'REPORT_USER_SPEND',
    lineItems: 'REPORT_ORDER_LINE_ITEMS',
  },
}

export const secondaryCategoryReportConfig: OrderReportConfig = {
  key: 'secondaryCategory',
  kind: ReportKinds.ORDERS,
  dimension: OrderReportDimensions.SECONDARY_CATEGORIES,
  titleKey: messages.secondaryCategoryReportTitle,
  sectionHeadingKey: messages.ordersSectionHeading,
  exportButtonLabelKey: messages.exportOrdersButtonLabel,
  exportTypeLabelKey: messages.orderItemsExportTypeLabel,
  chartHeadingKey: messages.topSpendChartHeading,
  detailNotFoundHeaderKey: messages.secondaryCategoryNotFoundHeader,
  tiles: [
    {
      labelKey: messages.totalSpendingTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalSecondaryCategoryUsageTileLabel,
      detailLabelKey: messages.totalAccountCodesUsedTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
    {
      labelKey: messages.totalItemsPurchasedTileLabel,
      detailLabelKey: messages.numberOfItemsPurchasedTileLabel,
      metric: ReportChartMetrics.COUNT,
      chartType: 'line',
      format: 'count',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.SPENT,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-account__account_code__code',
  aggregatePage: 'OrdersBySecondaryCategoryReportPage',
  detailPage: 'OrdersBySecondaryCategoryReportDetailsPage',
  referrerType: 'orders.department',
  paginationIds: {
    aggregate: 'REPORT_SECONDARY_CATEGORY_SPEND',
    lineItems: 'REPORT_ORDER_LINE_ITEMS',
  },
}

// "Top departments" fixes the Angular "TOP VENDORS" mis-title.
export const expenseReportConfig: ExpenseReportConfig = {
  key: 'expense',
  kind: ReportKinds.EXPENSES,
  titleKey: messages.expenseReportTitle,
  sectionHeadingKey: messages.expensesSectionHeading,
  exportButtonLabelKey: messages.exportExpenseItemsButtonLabel,
  exportTypeLabelKey: messages.expenseItemsExportTypeLabel,
  chartHeadingKey: messages.topSecondaryCategoriesChartHeading,
  detailNotFoundHeaderKey: messages.secondaryCategoryNotFoundHeader,
  tiles: [
    {
      labelKey: messages.totalReimbursementTileLabel,
      metric: ReportChartMetrics.REIMBURSABLE,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalExpenditureTileLabel,
      metric: ReportChartMetrics.SPENT,
      chartType: 'line',
      format: 'currency',
    },
    {
      labelKey: messages.totalSecondaryCategoryUsageTileLabel,
      detailLabelKey: messages.totalAccountCodesUsedTileLabel,
      metric: ReportChartMetrics.USAGE,
      chartType: 'bar',
      format: 'usage',
    },
  ],
  defaultDetailMetric: ReportChartMetrics.SPENT,
  aggregateDefaultSort: '-spent',
  detailDefaultSort: '-account__account_code__code',
  aggregatePage: 'ExpensesBySecondaryCategoryReportPage',
  detailPage: 'ExpensesBySecondaryCategoryReportDetailsPage',
  referrerType: 'expenses.department',
  paginationIds: {
    aggregate: 'REPORT_EXPENSE_SPEND',
    lineItems: 'REPORT_EXPENSE_LINE_ITEMS',
  },
}
